import * as React from 'react'

import { Box, Button, Form, Spinner } from '@factorialco/deprecated-design-system'
import { Field, Form as FormObject } from 'factorial-form'
import { DocumentsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DocumentForPreviewDocument,
  DocumentPreviewContentDocument,
  UpdateBasicInfoDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import InputText from 'modules/core/components/Inputs/Text'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'
import users from 'modules/core/store/collections/users'

import CoffreFortNumeriqueBlock from 'modules/coffrefortnumerique/components/Block'
import { Stateable } from 'modules/coffrefortnumerique/lib/enums'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import ResourceFieldCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import CustomFieldValuesCollection from 'modules/custom_resources/store/collections/customFields/values'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type { ICustomFieldValue } from 'modules/custom_resources/store/models/customFields/Value'
import Esignature from 'modules/documents/components/DocumentPreview/Content/Esignature'
import RestoreFromTrashBinBox from 'modules/documents/components/DocumentPreview/Content/Esignature/RestoreFromTrashBinBox'
import FillablePDF from 'modules/documents/components/DocumentPreview/Content/FillablePDF'
import FormLayout from 'modules/documents/components/DocumentPreview/FormLayout'
import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { isInTrashBin, isPendingToFill, isSignable } from 'modules/documents/lib/Documents'
import { isWritableEmployeeFolder } from 'modules/documents/lib/Folders'
import { ALL_TYPES } from 'modules/documents/lib/constants'
import { splitFileName } from 'modules/documents/lib/files/filenameUtils'
import DocumentForm from 'modules/documents/lib/forms/Document'
import employees from 'modules/employees/store/collections/employees'
import PrimoboxStatusRow from 'modules/integrations_primobox/components/StatusRow'

interface Props {
  document: Document
  saving?: boolean
  disableFilleableConfirm?: boolean
  contractVersions: ContractVersions
  loading?: boolean
}

const Content = ({
  document,
  saving,
  disableFilleableConfirm,
  contractVersions,
  loading,
}: Props) => {
  const [valuesCollection, setValuesCollection] =
    React.useState<CustomFieldValuesCollection | null>(null)
  const [resourceFieldsCollection, setResourceFieldsCollection] =
    React.useState<ResourceFieldCollection | null>(null)
  const [updateBasicInfo] = useMutation(UpdateBasicInfoDocument, {
    extractorFn: (data) => data.documentsMutations.updateBasicInfoDocumentsDocument,
  })
  const form = useUnsafeMemo(
    () =>
      new DocumentForm({
        filename: document.filename,
        folder_id: document.folder?.id,
      }),
    [document]
  )
  const employeeId = document.employee?.id
  const space = document.space

  const fetchCustomFields = async () => {
    if (document.folder?.id) {
      await fetchCustomFieldsData()
    }
  }
  const { loading: loadingCustomFields } = useAsyncData(async () => {
    await fetchCustomFields()
  }, [document.id])

  const { data, loading: loadingDocumentPreview } = useQuery(DocumentPreviewContentDocument, {
    variables: {
      companyId: document.company.id,
      documentId: document.id,
      space,
    },
  })

  const signatureRequests = data?.esignature.requestsConnection.nodes ?? []
  const folders = data?.documents.folders ?? []
  const features = data?.features.featuresConnection.nodes ?? []
  const primoboxDocumentStatus = data?.integrationsPrimobox.documentStatusesConnection?.nodes[0]

  const hasFeature = (featureName: string) =>
    features.find((feature) => feature.name === featureName)?.enabled

  const isLoading = loading || loadingDocumentPreview || loadingCustomFields

  const documentFolder = folders.find((folder) => folder.id === document.folder?.id)

  const employee = employees.find({
    id: employeeId,
  })

  const documentName = document.filename
  const { fileName, fileExtension } = splitFileName(documentName)

  const extensionForm = useMemo(
    () =>
      new FormObject(
        {
          filename: fileName,
        },
        {
          filename: 'string',
        }
      ),
    [fileName]
  )

  const onChangeFilename = (field: Field) => {
    const value = field.value
    form.get('filename').set(`${value}${fileExtension}`)
  }

  const legalEntity = legalEntities.find({
    id: employee && employeeLegalEntityId(employee),
  })

  const isCurrentUserASignee = !!document.signees?.find(
    (access_id) => access_id === employee?.attributes.access_id
  )

  const canManageThisFolder = folders.some(
    (folder) => folder.id === document.folder?.id && folder.managePermission
  )

  const canUserSeeSignatures =
    users.current.policy.can('documents.companyUseCompanyDocuments') ||
    canManageThisFolder ||
    isCurrentUserASignee ||
    signatureRequests.length > 0

  const showEsignature = isSignable(document) && canUserSeeSignatures && !isPendingToFill(document)

  const showRestoreDocument = isInTrashBin(document)

  const showDocumentFields = !isPendingToFill(document)

  const showCustomFields = document.folder?.id && !isPendingToFill(document)

  const showFillableStatus = isPendingToFill(document)

  const showCoffreFortNumerique =
    hasFeature('coffre_fort') &&
    legalEntity?.isFranceLegalEntity &&
    document.folder?.name === ALL_TYPES.payslip &&
    !isPendingToFill(document)

  const showPrimobox = hasFeature('integrations_primobox_active')

  const fetchCustomFieldsData = async () => {
    const valuesCollection = new CustomFieldValuesCollection([], {
      model_ref: 'document',
      instance_id: document.id,
    })

    const resourceFieldsCollection = new ResourceFieldCollection([], {
      model_ref: 'documents-folder',
      instance_id: form.get('folder_id').value,
    })

    const usageGroups = [CustomField.UsageGroups.documents, CustomField.UsageGroups.expenses]

    await Promise.all([
      valuesCollection.byUsageGroup(usageGroups),
      resourceFieldsCollection.byUsageGroup(usageGroups, employeeId),
    ])

    setValuesCollection(valuesCollection)
    setResourceFieldsCollection(resourceFieldsCollection)
  }

  const canEdit = (): boolean => {
    return (
      (document._policies?.edit.can || false) &&
      !document.folder?.showOnly &&
      !isInTrashBin(document)
    )
  }

  const createCustomValue = async (data: Record<string, unknown>): Promise<ICustomFieldValue> => {
    if (!valuesCollection) {
      throw new Error('CustomFieldValuesCollection is not initialized')
    }
    return valuesCollection.create(data)
  }

  const getResourceFields = () => {
    return resourceFieldsCollection?.models || []
  }

  const getValues = () => {
    return valuesCollection?.models || []
  }

  const writableFolders = employee
    ? folders.filter((folder) => isWritableEmployeeFolder(folder, employee))
    : folders

  const onSubmit = async () => {
    const { success } = await asyncAction(() => {
      const folderId = form.get('folder_id').value
        ? parseInt(form.get('folder_id').value)
        : undefined

      form.cleanAll()
      return updateBasicInfo({
        variables: {
          id: document.id,
          filename: form.get('filename').value,
          folderId: folderId,
        },
        refetchQueries: [DocumentsDataTableDocument, DocumentForPreviewDocument],
      })
    })

    if (success) {
      const fileNameWithoutExtension = splitFileName(form.get('filename').value).fileName
      extensionForm.get('filename').set(fileNameWithoutExtension)
    }
  }

  if (isLoading)
    return (
      <Box padding="s96" width="full" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Box paddingY="s32" paddingX="s24" gap="s48" width="full" flexGrow>
      {showRestoreDocument && (
        <RestoreFromTrashBinBox documentId={document.id} documentDeletedAt={document.deletedAt} />
      )}
      {showDocumentFields && (
        <FormLayout>
          <Form onSubmit={onSubmit} padding={null}>
            <InputText
              label={i18n.t('documents.modal.file_name')}
              field={extensionForm.get('filename')}
              readonly={!canEdit()}
              onChange={onChangeFilename}
            />
            <FolderSelector
              folders={writableFolders}
              {...(documentFolder ? { initialFolder: documentFolder } : {})}
              label={i18n.t('split_documents.form.select_label')}
              onChange={(value) => form.get('folder_id').set(value)}
              readOnly={!canEdit()}
            />
            {canEdit() && form.isDirty && (
              <Form.Buttons>
                <Button submit>{i18n.t('common.submit')}</Button>
              </Form.Buttons>
            )}
          </Form>
        </FormLayout>
      )}
      {showFillableStatus && (
        <FillablePDF
          saving={!!saving}
          disableFilleableConfirm={!!disableFilleableConfirm}
          document={document}
          signatureRequests={signatureRequests}
        />
      )}
      {showEsignature && <Esignature document={document} contractVersions={contractVersions} />}
      {showCoffreFortNumerique && (
        <Box style={{ margin: '-32px -64px' }}>
          <CoffreFortNumeriqueBlock id={document.id} type={Stateable.Document} />
        </Box>
      )}
      {showPrimobox && primoboxDocumentStatus && (
        <Box style={{ margin: '-32px -64px' }}>
          <PrimoboxStatusRow status={primoboxDocumentStatus} inModal />
        </Box>
      )}

      {showCustomFields && (
        <Box style={{ margin: '-32px -64px' }}>
          <CustomFieldsForm
            fieldValues={getValues()}
            resourceFields={getResourceFields()}
            createValue={createCustomValue}
            employee={employee}
            padFormDirection="vertical"
            boxed={false}
            title={i18n.t('documents.custom_fields.title')}
            description={i18n.t('documents.custom_fields.description')}
            contractVersions={contractVersions}
          />
        </Box>
      )}
    </Box>
  )
}

export default observer(Content)
